<script>
    import { createEventDispatcher, tick } from "svelte";
    import PlayerManagement from "./PlayerManagement.svelte";
    import ProgressBanner from "./ProgressBanner.svelte";
    import { genBonusId, addPlayersSequential } from "../../../utils/freebetsFlow.js";
    import { CAMPAIGN_STATUS, computeCampaignStatus } from "../../../utils/statusEngine.js";
    import {ICONS} from "../../../../js/icons.js";
    import { onMount } from "svelte"
  import { get } from "svelte/store";

    // ====================== Props (API) =====================
    export let campaign = null;
    export let actions = {
        addPlayer: async (_campaignId, _payload) => { throw new Error("addPlayer no inyectado"); },
        removePlayer: async (_campaignId, _playerId) => { throw new Error("removePlayer no inyectado"); },
    };
    export let getCampaignByCampaignId = async (_campaignId) => null;
    export let demoConfig = null;
    export let autoRefetchOnOpen = true;
    export let idStrategy = 'auto';
    export let showExtraActions = false;

    // ====================== Local variables =====================
    const dispatch = createEventDispatcher();
    let busy = false;
    let progress = null;     // { done, total } | null
    let showManagePlayers = false;
    let refreshing = false;

    // ====================== Reactive =====================
    $: titleName = (campaign?.campaignName || "Campaña").toString();
    $: titleId   = (campaign?.campaignId   || "").toString();
    $: header    = titleId ? `${titleName} (${titleId})` : titleName;
    $: players = Array.isArray(campaign?.playerList) ? campaign.playerList : [];
    $: assignedIds = players.map(p => String(p.playerId)).filter(Boolean);
    $: tablePlayers = (Array.isArray(players) ? players : []).map((p, i) => ({
        ...p,
        _row: i + 1,
        _info: statusInfo(p.playerStatus), // { label, class }
        _open: p._open || false
    }));

    // ===== Demo helpers =====
    function demoPlayers(cfg){ return Array.isArray(cfg?.PLAYERS) ? cfg.PLAYERS : []; }
    function demoCurrency(cfg){ return cfg?.DEFAULTS?.CURRENCY || ''; }
    $: demoClientCode = String(campaign?.client?.clientCode || "");
    $: demoClientId   = String(campaign?.client?.clientId   || "");
    $: isDemo = !!demoConfig && (
        demoClientCode === String(demoConfig?.CODE || "") ||
        demoClientId   === String(demoConfig?.ID   || "")
    );
    $: staticPlayersList = (isDemo && demoPlayers(demoConfig).length)
        ? demoPlayers(demoConfig).map(p => ({
            value: String(p.id),
            label: p.label || String(p.id),
            currency: String(p.currency || campaign?.currency || demoCurrency(demoConfig) || "")
        }))
        : null;

    // ====================== Const variables =====================
    const nextFrame = () => new Promise(requestAnimationFrame);
    const STATUS_KEY = { 0:"DELETED", 1:"ACTIVE", 2:"COMPLETED", 3:"EXPIRED", 4:"SCHEDULED" };
    const STATUS_META = {
        DELETED: {label: "Eliminado", class: "bg-secondary text-white"},
        ACTIVE: {label: "Activo", class: "bg-primary text-white"},
        COMPLETED: {label: "Completado", class: "bg-success text-white"},
        EXPIRED: {label: "Expirado", class: "bg-danger text-white"},
        SCHEDULED: {label: "Programado", class: "bg-warning text-dark"}
    };
    function statusInfo(code){
        const key = STATUS_KEY[Number(code) ?? 0] || "DELETED";
        return STATUS_META[key] || STATUS_META.DELETED;
    }
    function toggleRow(player) {
        const willOpen = !player._open;
        tablePlayers = tablePlayers.map(p => ({ ...p, _open: false }));
        if (willOpen) {
            tablePlayers = tablePlayers.map(p =>
                p.playerId === player.playerId
                    ? { ...p, _open: true }
                    : p
            );
        }
    }
    function mergeUniquePlayers(baseList, toAdd) {
        const seen = new Set((baseList || []).map(p => String(p.playerId)));
        const merged = [...(baseList || [])];
        for (const it of (toAdd || [])) {
            const id = String(it.playerId);
            if (!seen.has(id)) { merged.push(it); seen.add(id); }
        }
        return merged;
        
    }
    function updatePlayersOnParent(list){
        dispatch("playersUpdated", {
            campaignId: campaign?.campaignId || "",
            playerList: Array.isArray(list) ? list : []
        });
    }
    function getInitialPlayerStatus() {
        return Number(effectiveCampaignStatus) === CAMPAIGN_STATUS.ACTIVE
            ? CAMPAIGN_STATUS.ACTIVE   // 1
            : CAMPAIGN_STATUS.SCHEDULED; // 4 (default)
    }
    $: startMs = campaign?.startDate ? new Date(campaign.startDate).getTime() : 0;
    $: endMs   = campaign?.endDate   ? new Date(campaign.endDate).getTime()   : 0;
    $: baseCampaignStatus = Number(
        campaign?.baseStatus ?? campaign?.campaignStatus ?? campaign?.statusCode ?? CAMPAIGN_STATUS.SCHEDULED
           );
    $: effectiveCampaignStatus = computeCampaignStatus(baseCampaignStatus, startMs, endMs);
    function campaignIsClosed(){
          const s = Number(effectiveCampaignStatus);
          return s === CAMPAIGN_STATUS.DELETED || s === CAMPAIGN_STATUS.FINISHED;
    }

    // ====================== Actions =====================
    async function addPlayersToCampaign(items){
        if (!campaign?.campaignId || !items?.length) return;
        busy = true;
        progress = { done: 0, total: items.length, errors: 0 };
        await tick(); await nextFrame();
        try {
            await addPlayersSequential({
                campaignId: campaign.campaignId,
                players: items,
                genBonus: () => genBonusId(campaign.bonusPrefix),
                addPlayers: async (campaignId, payload) => {
                    try {
                        return await actions.addPlayer(campaignId, payload);   
                    } catch {
                        return await actions.addPlayer(campaignId, payload);
                    }
                },
                onTick: async ({ done, total, errors, payload, ok }) => {
                    progress = { done, total, errors };
                    await tick();
                    if (ok && Array.isArray(payload) && payload.length > 0) {
                        const row = payload.map(p => ({
                            bonusCode: p.bonusCode,
                            playerId: String(p.playerId),
                            playerName: p.playerName,
                            playerStatus: getInitialPlayerStatus()
                        }));
                        campaign = {
                            ...campaign,
                            playerList: mergeUniquePlayers(campaign.playerList, row)
                        };
                    }
                }
            });
            progress = { ...(progress || {}), done: items.length, total: items.length, errors: progress?.errors || 0 };
            await tick(); await nextFrame();
            updatePlayersOnParent(campaign.playerList || []);
        } catch (err) {
            console.error(err);
            alert("No se pudo agregar jugadores a la campaña.");
        } finally {
            busy = false;
            const p = progress;
            if (p && p.total > 0 && p.done >= p.total) {
                setTimeout(() => (progress = null), 700);
            } else {
                progress = null;
            }
        }
    }
    async function removePlayerFromCampaign(p, promo){
        if (!campaign?.campaignId) return;
        busy = true;
        try {
            await actions.removePlayer(campaign.campaignId, p, promo);
            if (promo?.bonusCode) {
                campaign = {
                    ...campaign,
                    playerList: (campaign.playerList || []).map(x => {
                        if (String(x.playerId) !== String(p.playerId)) return x;
                        return {
                            ...x,
                            deliveredPromos: (x.deliveredPromos || []).filter(
                                d => d.bonusCode !== promo.bonusCode
                            )
                        };
                    })
                };
            } else {
                campaign = {
                    ...campaign,
                    playerList: (campaign.playerList || []).filter(x => String(x.playerId) !== String(p.playerId))
                };
            }
            updatePlayersOnParent(campaign.playerList || []);
        } catch (err) {
            console.error(err);
            alert("No se pudo eliminar el jugador de la campaña.");
        } finally {
            busy = false;
        }
    }
    async function refetchOnOpen(){
        const id = campaign?.campaignId;
        if (!id || typeof getCampaignByCampaignId !== 'function') return;
        refreshing = true;
        try {
            const res = await getCampaignByCampaignId(id);
            const raw = res?.data ?? res;
            const fresh = Array.isArray(raw) ? raw[0] : raw;
            if (fresh) {
                const keep = { bonusPrefix: campaign?.bonusPrefix };
                campaign = { ...campaign, ...fresh, ...keep };
                dispatch("campaignRefreshed", { campaignId: id, doc: fresh });
            }
        } catch(_) {
        } finally {
            refreshing = false;
        }
    }
    // ======= Handlers =======
    function onAddPlayersClicked(){ showManagePlayers = true; }
    function onConfirmPlayers(e){
        const items = e.detail?.players || [];
        showManagePlayers = false;
        if (!items.length) return;
        addPlayersToCampaign(items);
    }
    function onRemove(p){
        if (!confirm(`¿Eliminar jugador ${p.playerName || p.playerId}?`)) return;
        removePlayerFromCampaign(p);
    }
    function onRemovePromo(p, promo){
        if (!confirm(`¿Eliminar bono ${promo.bonusCode} para ${p.playerName || p.playerId}?`)) return;
        removePlayerFromCampaign(p, promo);
    }
    function close(){ dispatch("close"); }
    onMount(() => { if (autoRefetchOnOpen) refetchOnOpen(); });

    function onView(player) {
        dispatch("viewPlayer", player);
    }

    // function onEdit(player) {
    //     dispatch("editPlayer", player);
    // }
</script>

<div class="modal-backdrop fade show"></div>
<div class="modal d-block" tabindex="-1" style="background:rgba(0,0,0,.3);">
    <div class="modal-dialog modal-lg modal-dialog-centered fixed-modal">
        <div class="modal-content modal-fixed">
            <!-- HEADER -->
            <div class="modal-header">
                <h5 class="modal-title">{header}</h5>
                <button type="button" class="btn-close" on:click={close} disabled={busy}></button>
            </div>

            <!-- BODY -->
            <div class="modal-body modal-body-fixed">
                <div class="top-actions d-flex justify-content-between align-items-center mb-2">
                    <div class="small text-muted">
                        Cliente: {campaign?.client?.clientName || '—'} ·
                        Moneda: {campaign?.currency || '—'} ·
                        Tipo: {campaign?.campaignType || '—'}
                    </div>
                    
                    {#if !showExtraActions}
                        <button class="btn btn-primary btn-sm"
                                on:click={onAddPlayersClicked}
                                disabled={busy || !campaign?.client?.clientId || !campaign?.currency || campaignIsClosed()}>
                            {@html (ICONS.addCircle)} Agregar jugadores
                        </button>
                    {/if}
                </div>
                <div class="body-scroll">
                    <div class="scroll-head">
                        {#if progress}
                            <ProgressBanner
                                    sticky={false}
                                    stage={(progress.done >= progress.total) ? 'done' : 'adding'}
                                    total={progress.total}
                                    done={progress.done}
                                    errors={progress.errors || 0}
                            />
                        {/if}
                    </div>
                    {#if !players.length}
                        <div class="empty">No hay jugadores en esta campaña aún.</div>
                    {:else}
                        <div class="table-responsive">
                            <table class="table table-striped table-hover table-bordered align-middle">
                                <thead>
                                <tr>
                                    <th>#</th>
                                    <th class="col-bonus-id">Bonus ID</th>
                                    <th>Jugador ID</th>
                                    <th>Jugador</th>
                                    <th>Status</th>
                                    <th>Acción</th>
                                </tr>
                                </thead>
                                <tbody>
                                {#each tablePlayers as p}
                                    <tr on:click={() => toggleRow(p)} class:p-open={p._open}>
                                        <td>{p._row}</td>
                                        <td class="col-bonus-id" title={Array.isArray(p.deliveredPromos) && p.deliveredPromos.length === 1 ? (p.deliveredPromos[0].bonusCode || '') : (p.bonusCode || '')}>
                                            {#if Array.isArray(p.deliveredPromos) && p.deliveredPromos.length > 0}
                                                {#if p.deliveredPromos.length === 1}
                                                    {p.deliveredPromos[0].bonusCode || '—'}
                                                {:else}
                                                    <span class="badge bg-info text-dark">Varios ({p.deliveredPromos.length})</span>
                                                {/if}
                                            {:else}
                                                {p.bonusCode || '—'}
                                            {/if}
                                        </td>
                                        <td>{p.playerId || '—'}</td>
                                        <td>{p.playerName || '—'}</td>
                                        <td><span class={`badge ${p._info.class}`}>{p._info.label}</span></td>
                                        <td>
                                            <div class="d-flex gap-1" role="group" aria-label="acciones">
                                                {#if showExtraActions}
                                                    <button class="btn btn-info btn-sm"
                                                            on:click|stopPropagation={() => onView(p)}
                                                            title="Visualizar">
                                                        {@html (ICONS.persons)}
                                                    </button>

                                                    <!-- <button class="btn btn-warning btn-sm"
                                                            on:click|stopPropagation={() => onEdit(p)}
                                                            title="Editar">
                                                        {@html (ICONS.edit)}
                                                    </button> -->
                                                {/if}

                                                <button class="btn btn-danger btn-sm"
                                                        on:click|stopPropagation={() => onRemove(p)}
                                                        disabled={busy || Number(p.playerStatus)===0 || Number(p.playerStatus)===2 || Number(p.playerStatus)===3}>
                                                    {@html (ICONS.delete)}
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                    {#if p._open && Array.isArray(p.deliveredPromos) && p.deliveredPromos.length > 0}
                                        <tr class="expanded-row">
                                            <td colspan="6">
                                                <div class="promos-card">
                                                    <div class="promos-title">Bonos entregados</div>
                                                    <div class="promos-list">
                                                        {#each p.deliveredPromos as promo}
                                                            {@const promoInfo = statusInfo(promo.status)}
                                                            <div class="promo-line">
                                                                <div class="promo-game">
                                                                    <span class="promo-label">Juego</span>
                                                                    <span class="game-chip">{promo.gameId || '—'}</span>
                                                                </div>
                                                                <div class="promo-bonus">
                                                                    <span class="promo-label">Bonus ID</span>
                                                                    <span class="bonus-text" title={promo.bonusCode}>{promo.bonusCode || '—'}</span>
                                                                </div>
                                                                <div class="promo-status">
                                                                    <span class="promo-label">Estado</span>
                                                                    <span class={`badge ${promoInfo.class}`}>{promoInfo.label}</span>
                                                                </div>
                                                                <div class="promo-action">
                                                                    <button class="btn btn-danger btn-sm"
                                                                        disabled={promo.status == 0}
                                                                        on:click={() => onRemovePromo(p, promo)}>
                                                                        {promo.status == 0 ? "Eliminado" : "Eliminar"}
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        {/each}
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    {/if}
                                {/each}
                                </tbody>
                            </table>
                        </div>
                    {/if}
                </div>
            </div>

            <!-- FOOTER -->
            <div class="modal-footer sticky-footer">
            <button class="btn btn-secondary" on:click={close} disabled={busy}>Cerrar</button>
            </div>
        </div>
    </div>
</div>

{#if showManagePlayers}
    <div class="nested-dim" aria-hidden="true"></div>
    <PlayerManagement
            clientId={campaign?.client?.clientId}
            currency={campaign?.currency}
            selectionMode="multiple"
            initialSelected={[]}
            idStrategy={idStrategy}
            excludeIds={assignedIds}
            staticPlayers={staticPlayersList}
            on:close={() => (showManagePlayers = false)}
            on:confirm={onConfirmPlayers}
    />
{/if}

<style>
    .fixed-modal{ max-width: 900px; }
    .modal-fixed{ height: 80vh; display: flex; flex-direction: column; }
    .modal-body-fixed{ flex: 1 1 auto; min-height: 0; display: flex; flex-direction: column; overflow: hidden; }
    .body-scroll{ position:relative; flex:1 1 auto; min-height:0; overflow:auto; }
    .scroll-head{ position: sticky; top: 0; z-index: 3; background: #fff; padding-top: .25rem; padding-bottom: .25rem; }
    .modal-footer.sticky-footer{ flex: 0 0 auto; position: sticky; bottom: 0; background: #fff; z-index: 1; border-top: 1px solid var(--bs-border-color, #e5e7eb); margin-top: 0; }
    .table-responsive{ max-height: unset; overflow: visible; }
    .empty{ flex: 1; display: grid; place-items: center; padding: 1rem; color: #777; border: 1px dashed #ddd; border-radius: 8px;}
    tr.p-open {
        background-color: #f2faff !important;
        border-left: 4px solid #0d6efd;
        transition: all 0.2s ease;
        cursor: pointer;
    }
    tr.p-open td {
        font-weight: 600;
    }
    tbody tr:not(.expanded-row):hover {
        cursor: pointer;
    }
    tbody tr:not(.expanded-row):not(.p-open):hover {
        background-color: #f8f9fa;
    }
    .expanded-row td {
        padding: 0;
        background-color: #fafbfc;
    }

    .col-bonus-id {
        max-width: 160px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .promos-card {
        padding: 0.75rem 1rem;
    }
    .promos-title {
        font-weight: 700;
        font-size: 0.85rem;
        color: #495057;
        margin-bottom: 0.5rem;
    }
    .promos-list {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
    }
    .promo-line {
        display: grid;
        grid-template-columns: minmax(100px, 1.2fr) minmax(120px, 1.5fr) 110px 90px;
        align-items: center;
        gap: 0.75rem;
        background: #fff;
        border: 1px solid #e9ecef;
        border-radius: 0.4rem;
        padding: 0.6rem 0.75rem;
    }
    .promo-label {
        display: block;
        font-size: 0.7rem;
        color: #6c757d;
        text-transform: uppercase;
        margin-bottom: 0.15rem;
    }
    .game-chip {
        display: inline-block;
        background: #e8f4fd;
        color: #0d6efd;
        border: 1px solid #b8daff;
        border-radius: 0.25rem;
        padding: 0.15rem 0.4rem;
        font-size: 0.8rem;
        font-weight: 600;
    }
    .bonus-text {
        font-size: 0.85rem;
        font-weight: 600;
        color: #333;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        display: block;
    }
    .promo-action {
        text-align: right;
    }
    @media (max-width: 768px) {
        .promo-line {
            grid-template-columns: 1fr 1fr;
            gap: 0.5rem;
        }
        .promo-action {
            grid-column: 1 / -1;
            text-align: left;
        }
    }
</style>
